import React, { useState, useEffect } from 'react';
import portalEventService from '../../services/portalEventService';
import { Calendar, MapPin, Clock } from 'lucide-react';

function PortalEvents() {
  const [events, setEvents] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const response = await portalEventService.getEvents(!showPast);
        setEvents(response.data.data || []);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    load();
  }, [showPast]);

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const eventTypeColors = {
    meeting: 'bg-blue-100 text-blue-800',
    court_date: 'bg-red-100 text-red-800',
    deadline: 'bg-orange-100 text-orange-800',
    consultation: 'bg-purple-100 text-purple-800',
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Events</h1>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show past events
        </label>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {events.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p>No upcoming events.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {events.map((event) => (
              <div key={event.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{event.title}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(event.start_time)}
                        {!event.all_day && ` ${formatTime(event.start_time)}`}
                        {event.end_time && !event.all_day && ` - ${formatTime(event.end_time)}`}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.location}
                        </span>
                      )}
                    </div>
                    {event.case_title && (
                      <p className="text-xs text-gray-400 mt-1">{event.case_title}</p>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${eventTypeColors[event.event_type] || 'bg-gray-100 text-gray-800'}`}>
                    {(event.event_type || '').replace('_', ' ')}
                  </span>
                </div>
                {event.description && (
                  <p className="text-sm text-gray-600 mt-2">{event.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalEvents;
